// =============================================================================
// Register Page - Creazione nuovo account
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { isDemoMode } from '../config/appConfig';
import { Lock, Mail, User, AlertCircle, Loader2 } from 'lucide-react';

export function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { register, isAuthenticated, isLoading, error, clearError } = useAuthStore();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // In modalità demo la registrazione è disabilitata: rimanda al login
  useEffect(() => {
    if (isDemoMode) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Redirect se già autenticato
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/onboarding', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear error on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const canSubmit =
    firstName.trim() && lastName.trim() && email && password && confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (password.length < 6) {
      setValidationError(t('register.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setValidationError(t('register.passwordMismatch'));
      return;
    }

    const success = await register({
      email,
      password,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });

    if (success) {
      // Nuovo utente senza organizzazioni → pagina di benvenuto
      navigate('/onboarding', { replace: true });
    }
  };

  const displayError = validationError || error;

  const inputClass =
    'block w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg ' +
    'focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ' +
    'placeholder:text-gray-400 transition-colors';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-green-50 overflow-y-auto">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-100 rounded-full opacity-50 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-green-100 rounded-full opacity-50 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md px-6 py-8">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <img src="/logo.svg" alt="Skedulab" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">SKEDULAB</h1>
          <p className="text-gray-500 mt-2">{t('tagline')}</p>
        </div>

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {t('register.title')}
          </h2>

          {/* Error Message */}
          {displayError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{displayError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nome + Cognome */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  {t('register.firstNameLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('register.firstNamePlaceholder')}
                    className={inputClass}
                    required
                    maxLength={100}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  {t('register.lastNameLabel')}
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('register.lastNamePlaceholder')}
                  className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                           placeholder:text-gray-400 transition-colors"
                  required
                  maxLength={100}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                {t('emailLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className={inputClass}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                {t('passwordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputClass}
                  required
                  minLength={6}
                  disabled={isLoading}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('register.passwordHint')}
              </p>
            </div>

            {/* Conferma Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                {t('register.confirmPasswordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputClass}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || !canSubmit}
              className="btn-primary w-full py-2.5 px-4 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('register.registering')}
                </>
              ) : (
                t('register.submitButton')
              )}
            </button>
          </form>

          {/* Link a login */}
          <p className="text-sm text-gray-500 text-center mt-6">
            {t('register.haveAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-emerald-600 hover:text-emerald-700"
            >
              {t('register.loginLink')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {t('copyright')}
        </p>
      </div>
    </div>
  );
}

export default RegisterPage;
